import {chooseLayout, DocumentWidgetData, DvLayout, getLeafLayouts, getSelectedLayout, getViewItemUrl, LayoutContentType, useAppDispatch, useAppSelector} from '@dv5/domain'
import {clipboardService, LayoutChooserList, LinkIcon} from '@dv5/components'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import './DocumentWidgetToolbar.less'

interface Props {
    data: DocumentWidgetData
    targetAvailable: boolean
    toolbarStyles: CSSProperties
    onNavigateToDocument: (documentId: string, layoutId: string) => void
}

export const DocumentWidgetToolbar: FC<Props> = ({
    data,
    targetAvailable,
    toolbarStyles,
    onNavigateToDocument,
}) => {
    const dispatch = useAppDispatch()
    const leafLayouts = useAppSelector(getLeafLayouts)
    const selectedLayout = useAppSelector(getSelectedLayout)
    const [openDropdown, setOpenDropdown] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpenDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleChooseLayout = (layout: DvLayout) => {
        dispatch(chooseLayout(layout))
        setOpenDropdown(false)
    }

    const handleNavigate = () => {
        if (!targetAvailable || !data.documentId || !selectedLayout) {
            return
        }
        onNavigateToDocument(data.documentId, selectedLayout.id)
    }

    const handleCopyLink = () => {
        if (!data.documentId || !selectedLayout) {
            return
        }
        clipboardService.copyTo(
            getViewItemUrl(window.location.origin, selectedLayout.id, data.documentId, LayoutContentType.DOCUMENT),
            false
        )
    }

    return (
        <div className='document-widget-toolbar' style={toolbarStyles} ref={dropdownRef}
             onMouseDown={(e) => e.stopPropagation()}>
            {leafLayouts.length > 0 && (
                <div className='document-widget-toolbar__layout-selector'>
                    <button
                        className={`document-widget-toolbar__button ${openDropdown ? 'document-widget-toolbar__button--active' : ''}`}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setOpenDropdown(!openDropdown)
                        }}
                        aria-label='Choose layout for navigation'
                        type='button'
                        title={selectedLayout ? selectedLayout.title : 'Select layout'}
                    >
                        <span className='document-widget-toolbar__layout-title'>
                            {selectedLayout ? selectedLayout.title : 'Layout'}
                        </span>
                        <span className='document-widget-toolbar__dropdown-caret'>▾</span>
                    </button>
                    {openDropdown && (
                        <div className='document-widget-toolbar__dropdown'>
                            <LayoutChooserList
                                layouts={leafLayouts}
                                selectedLayoutId={selectedLayout?.id}
                                onSelect={handleChooseLayout}
                            />
                        </div>
                    )}
                </div>
            )}
            {leafLayouts.length > 0 && <div className='document-widget-toolbar__divider'/>}
            <button
                className='document-widget-toolbar__button'
                onClick={handleCopyLink}
                disabled={!data.documentId || !selectedLayout}
                aria-label='Copy link'
                type='button'
                title='Copy link'
            >
                <LinkIcon />
            </button>
            <div className='document-widget-toolbar__divider'/>
            <button
                className='document-widget-toolbar__button document-widget-toolbar__button--navigate'
                onClick={handleNavigate}
                disabled={!targetAvailable || !data.documentId || !selectedLayout}
                aria-label='Open document in selected layout'
                type='button'
                title='Open document in selected layout'
            >
                →
            </button>
        </div>
    )
}
